import React from 'react';
import { useParams } from 'react-router-dom';
import SubPage from '#/common/components/SubPage';
import { utcToLocalString } from '#/common/utils/datetimeTransform';
import useSyncHistoryQuery from '../hooks/useSyncHistoryQuery';
import SyncHistoryForm from '../components/SyncHistoryForm';
import type { SyncHistoryFormRecord } from '../utils/fields';
import { syncStatus, syncType } from '../utils/constants';

export interface SyncHistoryParams {
  readonly id?: string;
}

const formatDateTime = (value?: string | null): string => {
  if (!value) return '';
  return utcToLocalString(value);
};

const formatResult = (value?: string | null): string => {
  if (!value) return '';

  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const emptySyncHistory: SyncHistoryFormRecord = {
  __typename: 'SyncHistoryItem',
  id: '',
  number: 0,
  displayNumber: '',
  source: '',
  type: '',
  status: '',
  syncedCount: 0,
  result: '',
  organizationName: '',
  syncedByUserName: '',
  createdAt: '',
  updatedAt: '',
  startedAt: '',
  finishedAt: '',
};

const SyncHistoryPage: React.FC = () => {
  const { id } = useParams() as SyncHistoryParams;
  const syncHistoryQuery = useSyncHistoryQuery(id);
  const syncHistory = syncHistoryQuery.data?.getSyncHistory?.data;

  const formRecord: SyncHistoryFormRecord = syncHistory
    ? {
        ...syncHistory,
        type: syncType[syncHistory.type as keyof typeof syncType]?.label ?? syncHistory.type,
        status:
          syncStatus[syncHistory.status as keyof typeof syncStatus]?.label ?? syncHistory.status,
        result: formatResult(syncHistory.result),
        organizationName: syncHistory.organization?.name ?? 'All Organizations',
        syncedByUserName: syncHistory.syncedByUser?.name ?? 'System',
        createdAt: formatDateTime(syncHistory.createdAt),
        updatedAt: formatDateTime(syncHistory.updatedAt),
        startedAt: formatDateTime(syncHistory.startedAt),
        finishedAt: formatDateTime(syncHistory.finishedAt),
      }
    : emptySyncHistory;

  const title = formRecord.displayNumber || 'Sync History';

  return (
    <SubPage title={title} isLoading={syncHistoryQuery.loading} showGoBackButton type="form">
      <SyncHistoryForm syncHistory={formRecord} />
    </SubPage>
  );
};

export default React.memo(SyncHistoryPage);
